<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-6">
        <FilterTabs
          label="View by"
          v-model="portfolioFilter"
          :options="tabOptions"
          @change="onTabChange"
        />
        <FilterDropdown
          v-if="portfolioFilter === 'By Group' && showGroupTypeSelector"
          label="Group Type"
          v-model="selectedEntityTypeName"
          :options="entityTypeOptions"
          @change="onGroupTypeChange"
        />
      </div>

      <!-- Engagement Score Summary & Filters -->
      <div class="flex items-center gap-4">
        <!-- Aggregate Score -->
        <div v-if="aggregateScore !== null" class="flex items-center gap-2 text-sm">
          <span class="text-gray-500">Avg Score:</span>
          <span class="font-semibold" :class="scoreColorClass">{{ Math.round(aggregateScore) }}</span>
        </div>

        <!-- Quick Filters -->
        <div class="flex items-center gap-1">
          <button
            v-for="filter in scoreFilters"
            :key="filter.key"
            @click="toggleScoreFilter(filter.key)"
            class="px-2 py-1 text-xs rounded-full border transition-colors"
            :class="activeScoreFilter === filter.key
              ? 'bg-primary-500 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="portfolioFilter === 'My Practices'">
      <Entity_Table
        v-if="userEntities.length > 0"
        :auto-height="false"
        :root-entity-id="userEntities[0].id"
        :score-filter="currentScoreFilter"
        :key="'my-' + entityTableKey"
      />
      <div v-else class="text-gray-500 text-sm py-4">
        No entities assigned to your user account.
      </div>
    </div>

    <div v-else-if="portfolioFilter === 'All Practices'">
      <Entity_Table
        :entity-type-name="selectedEntityTypeName"
        :auto-height="false"
        :score-filter="currentScoreFilter"
        :key="'all-' + entityTableKey"
      />
    </div>

    <div v-else-if="portfolioFilter === 'By Group'">
      <Entity_Table
        :auto-height="false"
        :key="'group-' + entityTableKey"
        :entity-type-name="selectedEntityTypeName"
        :score-filter="currentScoreFilter"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Entity_Table from '@/components/Entity/Entity_Table.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'TabbedEntitySelector',
  components: {
    Entity_Table,
    FilterTabs,
    FilterDropdown,
  },
  props: {
    settings: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const localStorageKey = this.settings?.localStorage_key || 'engage_entity_tab'
    const defaultTab = this.settings?.default_tab || 'By Group'

    return {
      userEntities: [],
      entityTypes: [],
      entityTableKey: 0,
      portfolioFilter: localStorage.getItem(localStorageKey) || defaultTab,
      selectedEntityTypeName: localStorage.getItem('engage_selected_entity_type_name') || 'Practice',
      aggregateScore: null,
      activeScoreFilter: null,
      scoreFilters: [
        { key: 'low', label: 'Low (<40)', min: 0, max: 40 },
        { key: 'med', label: 'Med (40-80)', min: 40, max: 80 },
        { key: 'high', label: 'High (80+)', min: 80, max: 101 }
      ]
    }
  },
  computed: {
    tabOptions() {
      return [
        { value: 'My Practices', label: 'My Practices' },
        ...(canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL)
          ? [{ value: 'All Practices', label: 'All Practices' }, { value: 'By Group', label: 'By Group' }]
          : []),
      ]
    },
    entityTypeOptions() {
      return (this.entityTypes || []).map(et => ({ value: et.type_name, label: et.type_name }));
    },
    showGroupTypeSelector() {
      return this.settings?.show_group_type_selector !== false
    },
    localStorageKey() {
      return this.settings?.localStorage_key || 'engage_entity_tab'
    },
    scoreColorClass() {
      if (this.aggregateScore === null) return 'text-gray-400'
      if (this.aggregateScore >= 80) return 'text-green-600'
      if (this.aggregateScore >= 60) return 'text-yellow-600'
      if (this.aggregateScore >= 40) return 'text-orange-600'
      return 'text-red-600'
    },
    currentScoreFilter() {
      if (!this.activeScoreFilter) return null
      return this.scoreFilters.find(f => f.key === this.activeScoreFilter)
    }
  },
  methods: {
    async fetchUserEntities() {
      try {
        // Get the user's directly linked entity (not all descendants)
        const response = await axios.get('/api/entities/', {
          params: { linked_user_id: 'me' }
        })
        const results = response.data.results || response.data
        this.userEntities = results
      } catch (error) {
        console.error('Error fetching user entities:', error)
        this.userEntities = []
      }
    },
    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/')
        this.entityTypes = response.data
          .filter(type => type.is_active)
          .sort((a, b) => a.type_name.localeCompare(b.type_name))
      } catch (error) {
        console.error('Error fetching entity types:', error)
        this.entityTypes = []
      }
      // Reconcile the selected group type against THIS tenant's actual types.
      // The stored/default value may be 'Practice' (or a leftover from another
      // tenant) which doesn't exist here (e.g. IHCI uses Service Area / TIN /
      // Department), causing entity_tree to 404 'Entity type "Practice" not found'.
      this.ensureValidGroupType()
    },
    ensureValidGroupType() {
      const names = (this.entityTypes || []).map(et => et.type_name)
      if (!names.length) return
      if (!names.includes(this.selectedEntityTypeName)) {
        // Prefer a sensible org-grouping type if present, else first available.
        const preferred = ['Practice', 'Service Area', 'TIN', 'Department', 'Region', 'Pod']
          .find(n => names.includes(n))
        this.selectedEntityTypeName = preferred || names[0]
        localStorage.setItem('engage_selected_entity_type_name', this.selectedEntityTypeName)
      }
    },
    onTabChange(value) {
      localStorage.setItem(this.localStorageKey, value)
    },
    onGroupTypeChange() {
      localStorage.setItem('engage_selected_entity_type_name', this.selectedEntityTypeName)
      this.entityTableKey++
    },
    async fetchAggregateScore() {
      try {
        const response = await axios.post('/api/entity-engagement-scores/query/', {
          entity_type: this.selectedEntityTypeName || 'Practice',
          aggregate_entities: true,
          aggregation: 'avg'
        })
        if (response.data?.composite_score !== undefined) {
          this.aggregateScore = response.data.composite_score
        } else if (response.data?.time_series?.length > 0) {
          // Handle time_series response format
          const latest = response.data.time_series[response.data.time_series.length - 1]
          this.aggregateScore = latest.composite_score
        }
      } catch (error) {
        console.error('Error fetching aggregate score:', error)
      }
    },
    toggleScoreFilter(filterKey) {
      if (this.activeScoreFilter === filterKey) {
        this.activeScoreFilter = null
      } else {
        this.activeScoreFilter = filterKey
      }
      this.entityTableKey++
    }
  },
  async mounted() {
    // Resolve entity types first so the group type is reconciled against this
    // tenant before the aggregate-score query uses it.
    await Promise.all([
      this.fetchUserEntities(),
      this.fetchEntityTypes()
    ])
    await this.fetchAggregateScore()
  }
}
</script>
